/**
 * Extracts the properties related to panels from the given UI object.
 * This function is useful for isolating the logic of this transformation,
 * which makes it easier to test independently from the rest of the code.
 */
export type PanelsProperties = {
  isRightPanelOpen: boolean;
  isLeftPanelOpen: boolean;
  isBottomPanelOpen: boolean;
  currentLeftPanelWidth: number | undefined;
  currentRightPanelWidth: number | undefined;
  currentBottomPanelHeight: number | undefined;
};

const getPanelPropertiesAndDimensions = ({
  isRightPanelOpen,
  isLeftPanelOpen,
  isBottomPanelOpen,
  currentLeftPanelWidth = 0,
  currentRightPanelWidth = 0,
  currentBottomPanelHeight = 0,
}: PanelsProperties): PanelsProperties & { left: number; right: number; bottom: number } => ({
  isRightPanelOpen,
  isLeftPanelOpen,
  isBottomPanelOpen,
  currentLeftPanelWidth,
  currentRightPanelWidth,
  currentBottomPanelHeight,
  left: isLeftPanelOpen ? currentLeftPanelWidth : 0,
  right: isRightPanelOpen ? currentRightPanelWidth : 0,
  bottom: isBottomPanelOpen ? currentBottomPanelHeight : 0,
});

/** Destination insets for a fit that should not read live panel measurements. */
export const panelPropertiesFromInsets = ({
  left = 0,
  right = 0,
  bottom = 0,
}: { left?: number; right?: number; bottom?: number }) => getPanelPropertiesAndDimensions({
  isLeftPanelOpen: left > 0,
  isRightPanelOpen: right > 0,
  isBottomPanelOpen: bottom > 0,
  currentLeftPanelWidth: left,
  currentRightPanelWidth: right,
  currentBottomPanelHeight: bottom,
});

export default getPanelPropertiesAndDimensions;
